import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AdjustStockInput, CreateInventoryItemInput, UpdateInventoryItemInput } from '@/lib/marina/validators'

export type ThresholdStatus = 'GOOD' | 'LOW' | 'OUT'

export interface InventoryItem {
  id: string
  name: string
  category: string | null
  initialStock: string
  currentStock: string
  lowThreshold: string
  unit: string
  notes: string | null
  thresholdStatus?: ThresholdStatus
  createdAt: string
  updatedAt: string
  movements?: {
    id: string
    delta: string
    reason: string | null
    createdAt: string
  }[]
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function jsonFetch<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

export function useInventory(params: { q?: string; category?: string } = {}) {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, String(v))
  return useQuery({
    queryKey: ['inventory', params],
    queryFn: () =>
      apiFetch<{ items: InventoryItem[]; total: number }>(`/api/inventory${qs.toString() ? `?${qs}` : ''}`),
    staleTime: 15_000,
  })
}

export function useInventoryItem(id: string | null | undefined) {
  return useQuery({
    queryKey: ['inventory', id],
    queryFn: () => apiFetch<{ item: InventoryItem }>(`/api/inventory/${id}`),
    enabled: !!id,
  })
}

export function useCreateInventoryItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateInventoryItemInput) =>
      jsonFetch<{ item: InventoryItem }>('/api/inventory', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory'] }),
  })
}

export function useUpdateInventoryItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInventoryItemInput }) =>
      jsonFetch<{ item: InventoryItem }>(`/api/inventory/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['inventory'] })
      qc.invalidateQueries({ queryKey: ['inventory', v.id] })
    },
  })
}

export function useDeleteInventoryItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => jsonFetch<{ ok: boolean }>(`/api/inventory/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inventory'] }),
  })
}

export function useAdjustStock() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AdjustStockInput }) =>
      jsonFetch<{ item: InventoryItem }>(`/api/inventory/${id}/movements`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['inventory'] })
      qc.invalidateQueries({ queryKey: ['inventory', v.id] })
    },
  })
}
